import { useMemo, useCallback } from "react"
import { Box, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useDining } from "hooks/dining/useDining"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { PageCard, PageCardContent } from "components/card"
import { VenueSelector } from "components/VenueSelector"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { MotionDiv } from "components/motions"
import { EmptyData } from "components/EmptyData"
import { DiningIcon } from "components/icons"
import { ImageSkeletonCard } from "components/ImageCard"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useElementScrollRestoration } from "hooks/scrollbar/useElementScrollRestoration"
import {
  DiningCard as StandardDiningCard,
  DiningGroupLayout,
  DiningListLayout,
  IDiningCardProps,
} from "components/domains/dining"
import { IContentWithAsideProps } from "layouts/kiosk-h/ContentWithAside"

interface IDiningKioskHPageProps {
  Layout?: React.FC<IContentWithAsideProps>
  AppNavigation?: React.ComponentType
  DiningCard?: React.FC<IDiningCardProps>
}

export const DiningKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  DiningCard = StandardDiningCard,
}: IDiningKioskHPageProps) => {
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasData,
    isFetched,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
    layout,
  } = useDining()
  const theme = useTheme()
  const { t } = useTranslation()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )
  const handleChangeCategory = useCallback(
    (e, id) => {
      if (id === null) return
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const showSubCategoryFilter = categories.length > 0
  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}

  const {
    pageCard,
    pageCardContent,
    contentWrapper,
    gridContainer,
    gridItem,
    venueSelector,
    filterSection,
    scrollPane,
    emptyDataWrapper,
    diningImageCard,
    subCategoryFilterBar,
    levelHeader,
    divider,
  } = styleOverrides

  const isGroupLayout = layout === "group"

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const { scrollRef } = useElementScrollRestoration<HTMLDivElement>(
    "dining-page-scroll-pane"
  )

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard} className={hasData ? "" : "emptyList"}>
        <PageCardContent sx={pageCardContent}>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            <Box
              ref={scrollRef}
              sx={{
                ...gridContainer,
                gridAutoFlow: isGroupLayout ? "" : "dense",
              }}
              className={showSubCategoryFilter || hasData ? "" : "emptyList"}
            >
              {hasData ? (
                <>
                  {isGroupLayout ? (
                    <DiningGroupLayout
                      pages={restaurants.pages}
                      gridItemSx={gridItem}
                      levelHeaderSx={levelHeader}
                      dividerSx={divider}
                      diningCardSx={diningImageCard}
                      DiningCard={DiningCard}
                    />
                  ) : (
                    <DiningListLayout
                      pages={restaurants.pages}
                      gridItemSx={gridItem}
                      diningCardSx={diningImageCard}
                      DiningCard={DiningCard}
                    />
                  )}
                  {hasNextPage &&
                    Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                      <ImageSkeletonCard
                        ref={
                          index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                        }
                        key={index}
                        sx={gridItem}
                      />
                    ))}
                </>
              ) : !isFetched ? (
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <ImageSkeletonCard key={index} sx={gridItem} />
                ))
              ) : (
                <Box sx={emptyDataWrapper}>
                  <EmptyData
                    icon={emptyIcon}
                    primary={t("Coming soon")}
                    secondary={t("Stay tuned for the update")}
                  />
                </Box>
              )}
            </Box>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            {showSubCategoryFilter && (
              <Box sx={filterSection}>
                <HorizontalScrollPane
                  stackProps={{
                    sx: scrollPane,
                  }}
                >
                  <CategoryFilterBar
                    sx={subCategoryFilterBar}
                    activeCategory={currentCategory}
                    categories={categories}
                    renderFilterButton={CategoryFilterChip}
                    onChange={handleChangeCategory}
                    enableSeparateBorder={false}
                  />
                </HorizontalScrollPane>
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
